import type { ApiAreaOfFocus } from '#/api/types/ApiAreaOfFocus';
import type { ApiJobFunction } from '#/api/types/ApiJobFunction';
import type { AppConfig } from '#/config/types/AppConfig';
import { searchAlgoliaOptionsWithSearchLocation } from '#/modules/search/algolia/options/searchAlgoliaOptionsWithSearchLocation';
import { getSearchAlgoliaDefaultConfig } from '#/modules/search/algolia/searchAlgoliaConfig';
import { searchAlgoliaIndex } from '#/modules/search/algolia/searchAlgoliaIndex';
import type { SearchAlgoliaRecommendedListingHit } from '#/modules/search/algolia/types/SearchAlgoliaRecommendedListingHit';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';

const ATTRIBUTES: (keyof SearchAlgoliaRecommendedListingHit)[] = [
  'objectID',
  'name',
  'city',
  'state',
  'logo',
  'orgName',
  'url',
];

type Props = {
  searchLocation: Pick<SearchLocation, 'latitude' | 'longitude'> &
    Partial<Omit<SearchLocation, 'latitude' | 'longitude'>>;
  options: {
    onlyWithLogo: boolean;
    jobFunction?: ApiJobFunction | null | undefined;
    areaOfFocus?: ApiAreaOfFocus | null | undefined;
    jobTitle?: string;
    locale?: string;
    familyId?: string;
    query?: string;
  };
  searchOptions?: { distinct: boolean };
  appConfig: AppConfig;
};

export async function searchAlgoliaFetchRecommendedJobs({
  searchLocation,
  options: {
    onlyWithLogo,
    jobFunction,
    areaOfFocus,
    locale,
    jobTitle,
    familyId,
  },
  searchOptions,
  appConfig,
}: Props) {
  const filters = [
    onlyWithLogo && '_tags:hasLogo',
    jobFunction && `(functions:'${jobFunction}')`,
    areaOfFocus && `(areasOfFocus: '${areaOfFocus}')`,
    locale && `(locale: '${locale}')`,
    familyId && `(compdb.familyId:'${familyId}')`,
  ]
    .filter(Boolean)
    .join(' AND ');

  return algoliaSearch<SearchAlgoliaRecommendedListingHit>({
    config: getSearchAlgoliaDefaultConfig({ appConfig }),
    query: jobTitle ?? '',
    indexName: searchAlgoliaIndex({ sort: 'newest', appConfig }),
    options: searchAlgoliaOptionsWithSearchLocation({
      options: {
        hitsPerPage: 20,
        attributesToRetrieve: ATTRIBUTES as string[],
        facetFilters: [['type:JOB']],
        filters,
        query: jobTitle,
        ...searchOptions,
      },
      searchLocation,
      radius: '',
    }),
    isUsingAlgoliaMock: appConfig.algolia.mock,
  }).then(({ hits }) => hits);
}
